import { logger } from '@/utils';
import { Injectable, Inject } from '@kaokei/use-vue-service';
import ysFixWebmDuration from 'fix-webm-duration';
import { UserService } from '../user.service';
import { useRoute } from 'vue-router';
import { getMediaConfig } from '@/utils/device/mediaConfig';

interface RecordData {
  data: Blob;
  name: string;
}

@Injectable()
export class MediaService {
  route: any = useRoute();
  public stream?: MediaStream; // 视频流和音频流
  public mediaRecorder?: MediaRecorder; // 实例上只有一个录制视频的recorder
  public error?: Event; // 错误信息

  @Inject(UserService)
  public userService!: UserService;

  // public constraints = {
  //   audio: true,
  //   video: {
  //     width: { min: 640, ideal: 640, max: 640 },
  //     height: { min: 480, ideal: 480, max: 480 },
  //     framerate: 60,
  //   },
  // };
  public get constraints() {
    const mediaConfig = getMediaConfig();
    return mediaConfig;
  }

  public getMediaStream() {
    this.reset();
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia(this.constraints)
        .then(mstream => {
          this.stream = mstream;

          mstream.getTracks().forEach(track => {
            if (track.kind == 'audio') {
              track.onended = event => {
                this.error = event;
                this.dispose();
              };
            }
            if (track.kind == 'video') {
              track.onended = event => {
                this.error = event;
                this.dispose();
              };
            }
          });

          this.stream.addEventListener('inactive', () => {
            window.hetaoTrack('element_show', {
              elementName: '赛考监控状态',
              statusText: 'inactive',
              live_status: 'inactive',
              userId: this.userService?.user?.userId || 0,
              uniqueId: this.route.params?.id ?? -1,
            });
          });
          window.hetaoTrack('element_show', {
            elementName: '赛考监控状态',
            statusText: 'success',
            live_status: 'success',
            userId: this.userService?.user?.userId || 0,
            uniqueId: this.route.params?.id ?? -1,
          });

          resolve(mstream);
        })
        .catch(e => {
          window.hetaoTrack('element_show', {
            elementName: '赛考监控状态',
            statusText: e?.message || 'error',
            live_status: 'error',
            userId: this.userService?.user?.userId || 0,
            uniqueId: this.route.params?.id ?? -1,
          });
          reject(e);
        });
    });
  }

  /**
   * 记录duration秒时间段的视频数据
   *
   * @param {number} duration
   * @memberof MediaService
   */
  public recordMedia(duration: number) {
    const startTime = window.performance.now();

    const [options, containerType] = this.getMediaRecorderOptions();

    if (this.stream) {
      this.mediaRecorder = options
        ? new MediaRecorder(this.stream, options as MediaRecorderOptions)
        : new MediaRecorder(this.stream);
    }

    let forceStop = false; // 强制停止音视频则不会上传音视频数据

    let durationTimer = 0; // 记录音视频的定时器

    const ret = {} as {
      result: Promise<RecordData>;
      stop: (isForceStop: boolean) => void; // 可以在业务代码中直接取消视频录制
    };

    ret.stop = (isForceStop: boolean) => {
      forceStop = isForceStop;
      window.clearTimeout(durationTimer);
      if (this.mediaRecorder) {
        try {
          this.mediaRecorder.stop();
        } catch (e) {
          // add sentry mark
          // console.log('setTag', 'api', 'stopMediaRecorder')
          logger.debug('主动停止音视频异常 => ', e);
        }
      }
    };

    ret.result = new Promise((resolve, reject) => {
      if (!this.stream || !this.mediaRecorder) {
        reject('音视频流不存在');
        return;
      }

      const chunks: BlobPart[] = []; // 录制音视频的数据
      this.mediaRecorder.ondataavailable = e => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        if (!this.stream) {
          reject('音视频流不存在');
          return;
        }

        if (forceStop) {
          reject('强制停止录制音视频');
        } else if (this.mediaRecorder) {
          const endTime = window.performance.now();
          const realDuration = endTime - startTime;
          const recordedBlob = new Blob(chunks, {
            type: this.mediaRecorder.mimeType,
          });

          if (containerType === 'video/webm') {
            ysFixWebmDuration(recordedBlob, realDuration, (fixedBlob: Blob) => {
              resolve({
                data: fixedBlob,
                name: 'video.webm',
              });
            });
          } else {
            resolve({
              data: recordedBlob,
              name: 'video.mp4',
            });
          }
        } else {
          reject('mediaRecorder不存在');
        }
      };

      this.mediaRecorder.onerror = e => {
        reject(e);
        window.clearTimeout(durationTimer);
        try {
          if (this.mediaRecorder) {
            this.mediaRecorder.stop();
          }
        } catch (e) {
          reject('停止录制音视频异常');
          // add sentry mark
          // console.log('setTag', 'api', 'recordMedia onerror');
        }
      };

      // 每隔1秒记录一次数据
      this.mediaRecorder.start(1000);

      // 超时后自动停止记录数据
      durationTimer = window.setTimeout(() => {
        try {
          if (this.mediaRecorder) {
            this.mediaRecorder.stop();
          }
        } catch (e) {
          reject('停止录制音视频异常');
          // add sentry mark
          // console.log('setTag', 'api', 'recordMedia setTimeout error');
        }
      }, duration * 1000);
    });
    return ret;
  }

  public getMediaRecorderOptions() {
    let options: MediaRecorderOptions | undefined;
    let containerType = 'video/webm';

    if (typeof MediaRecorder.isTypeSupported == 'function') {
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        options = { mimeType: 'video/webm;codecs=vp9' };
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=h264')) {
        options = { mimeType: 'video/webm;codecs=h264' };
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        options = { mimeType: 'video/webm' };
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        containerType = 'video/mp4';
        options = { mimeType: 'video/mp4' };
      }
    }

    return [options, containerType];
  }

  public reset() {
    this.stream = undefined;
    this.error = undefined;
  }

  public dispose() {
    logger.warn('warn media service dispose => 1', new Date().toLocaleString());
    if (this.stream) {
      logger.warn(
        'warn media service dispose => 2',
        new Date().toLocaleString()
      );
      try {
        logger.warn(
          'warn media service dispose => 3',
          new Date().toLocaleString()
        );
        this.stream.getTracks().forEach(track => track.stop());
        logger.warn(
          'warn media service dispose => 4',
          new Date().toLocaleString()
        );
      } catch (e) {
        logger.warn(
          'warn media service dispose => 5',
          new Date().toLocaleString()
        );
        // add sentry mark
        // console.log('setTag', 'dispose mediaService');
        logger.debug('dispose中停止音视频异常 => ', e);
      }
    }
    this.stream = undefined;
    this.mediaRecorder = undefined;
  }
}
